(() => {
  const roots = document.querySelectorAll('[data-nf-katana-parts-explorer]');
  if (!roots.length) return;

  const pushEvent = (event, details = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...details });
  };

  const initialize = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    root.classList.add('is-enhanced');

    const groupButtons = [...root.querySelectorAll('[data-group-filter]')];
    const viewButtons = [...root.querySelectorAll('[data-view-control]')];
    const partButtons = [...root.querySelectorAll('[data-part-button]')];
    const definitions = [...root.querySelectorAll('[data-part-definition]')];
    const canvases = [...root.querySelectorAll('[data-view-canvas]')];
    const explodeButton = root.querySelector('[data-explode-control]');
    const explodeLabel = root.querySelector('[data-explode-label]');
    const explodedLabel = root.querySelector('[data-exploded-label]');
    const activeLabel = root.querySelector('[data-active-label]');
    const viewLabel = root.querySelector('[data-view-label]');
    const status = root.querySelector('[data-explorer-status]');
    const clearButton = root.querySelector('[data-clear-selection]');
    const emptyPanel = root.querySelector('[data-empty-panel]');
    const visual = root.querySelector('.nf-katana-parts-explorer__visual');
    const svgNamespace = 'http://www.w3.org/2000/svg';
    const createSvgElement = (name, className) => {
      const element = document.createElementNS(svgNamespace, name);
      if (className) element.setAttribute('class', className);
      return element;
    };
    const callout = (() => {
      if (!visual) return null;
      const svg = createSvgElement('svg', 'nf-katana-parts-explorer__callout');
      const leader = createSvgElement('polyline', 'nf-katana-parts-explorer__callout-leader');
      const arrow = createSvgElement('polyline', 'nf-katana-parts-explorer__callout-arrow');
      const anchor = createSvgElement('circle', 'nf-katana-parts-explorer__callout-anchor');
      const box = createSvgElement('rect', 'nf-katana-parts-explorer__callout-box');
      const term = createSvgElement('text', 'nf-katana-parts-explorer__callout-term');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.append(leader, arrow, anchor, box, term);
      visual.append(svg);
      return { svg, leader, arrow, anchor, box, term };
    })();
    const selectedGroups = new Set();
    let started = false;
    let completed = false;
    let activeGroup = 'blade';
    let lockedPart = '';
    let lastTrigger = null;
    let pointerActivation = null;
    let mobileReturnToken = 0;
    let mobileReturnFrame = 0;
    let mobileReturnPhase = 'idle';
    let renderedCalloutButton = null;

    const start = () => {
      if (started) return;
      started = true;
      pushEvent('katana_parts_explorer_start');
    };

    const loadImage = (image) => {
      if (!image) return Promise.resolve();
      if (!image.getAttribute('src')) image.setAttribute('src', image.dataset.src || '');
      if (image.complete && image.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve, reject) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', reject, { once: true });
      });
    };

    const ensureViewAssets = async (view) => {
      const canvas = canvases.find((candidate) => candidate.dataset.viewCanvas === view);
      if (!canvas) return;
      const images = [...canvas.querySelectorAll('[data-full-image], [data-highlight-for]')];
      try {
        await Promise.all(images.map(loadImage));
        canvas.classList.add('has-full-image');
      } catch (_error) {
        canvas.classList.remove('has-full-image');
      }
    };

    const ensureExplodedAssets = async () => {
      if (root.classList.contains('layers-ready')) return true;
      const layers = [...root.querySelectorAll('[data-layer-stack="drawn"] [data-explode-layer]')];
      root.classList.add('layers-loading');
      try {
        await Promise.all(layers.map(loadImage));
        root.classList.remove('layers-loading');
        root.classList.add('layers-ready');
        return true;
      } catch (_error) {
        root.classList.remove('layers-loading');
        root.classList.remove('layers-ready');
        if (status) status.textContent = root.dataset.layerError || '';
        return false;
      }
    };

    const activeButton = () => partButtons.find((button) => button.dataset.partId === lockedPart);

    const getHotspotCoordinates = (button) => {
      const isExploded = root.dataset.exploded === 'true' && root.dataset.view === 'drawn';
      return {
        x: Number(button.dataset.partX || 50) + (isExploded ? Number(button.dataset.explodeDx || 0) / 16 : 0),
        y: Number(button.dataset.partY || 50) + (isExploded ? Number(button.dataset.explodeDy || 0) / 3.6 : 0),
      };
    };

    const hideCallout = () => {
      renderedCalloutButton = null;
      if (!callout) return;
      callout.svg.removeAttribute('data-callout-part');
      callout.svg.setAttribute('viewBox', '0 0 1 1');
      callout.leader.removeAttribute('points');
      callout.arrow.removeAttribute('points');
      callout.term.textContent = '';
    };

    const setCallout = (button) => {
      const hasVisibleHotspot = Boolean(
        callout
        && visual
        && button
        && button.dataset.hotspot === 'true'
        && button.dataset.partState === root.dataset.view,
      );
      if (!hasVisibleHotspot) {
        hideCallout();
        return false;
      }

      const width = Math.max(1, visual.clientWidth);
      const height = Math.max(1, visual.clientHeight);
      const visualRect = visual.getBoundingClientRect();
      const { x, y } = getHotspotCoordinates(button);
      const targetX = Math.max(0, Math.min(width, (x / 100) * width));
      const targetY = Math.max(0, Math.min(height, (y / 100) * height));
      const mobile = width <= 600;
      const shortViewport = window.innerHeight <= 500 && window.innerWidth <= 700;
      const labelHeight = mobile ? 38 : 36;
      const bandInset = mobile ? 38 : 42;
      const horizontalOffset = Math.min(mobile ? 72 : 110, width * 0.18);

      callout.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      callout.term.textContent = button.dataset.partName || '';
      let measuredText = 0;
      try { measuredText = callout.term.getComputedTextLength(); } catch (_error) { measuredText = 0; }
      const fallbackText = (button.dataset.partName || '').length * (mobile ? 9 : 8.5);
      const labelWidth = Math.max(92, Math.min(Math.min(width - 24, 220), Math.max(measuredText, fallbackText) + 28));
      const rightOverlayInset = window.innerWidth <= 700 ? 96 : 18;
      const visibleLeft = Math.max(12, 12 - visualRect.left);
      const visibleRight = Math.min(width - 12, window.innerWidth - visualRect.left - rightOverlayInset);
      const minimumCenterX = visibleLeft + (labelWidth / 2);
      const maximumCenterX = visibleRight - (labelWidth / 2);
      const proposedCenterX = targetX < width / 2 ? targetX + horizontalOffset : targetX - horizontalOffset;
      const labelCenterX = maximumCenterX >= minimumCenterX
        ? Math.max(minimumCenterX, Math.min(maximumCenterX, proposedCenterX))
        : Math.max(12 + (labelWidth / 2), Math.min(width - 12 - (labelWidth / 2), proposedCenterX));
      const topOverlayInset = shortViewport ? 82 : 12;
      const bottomOverlayInset = shortViewport ? 96 : 12;
      const visibleTop = Math.max(12, topOverlayInset - visualRect.top);
      const visibleBottom = Math.min(height - 12, window.innerHeight - visualRect.top - bottomOverlayInset);
      const proposedCenterY = shortViewport ? visibleTop + (labelHeight / 2) : (y >= 50 ? bandInset : height - bandInset);
      const minimumCenterY = visibleTop + (labelHeight / 2);
      const maximumCenterY = visibleBottom - (labelHeight / 2);
      const labelCenterY = maximumCenterY >= minimumCenterY
        ? Math.max(minimumCenterY, Math.min(maximumCenterY, proposedCenterY))
        : Math.max(labelHeight / 2 + 12, Math.min(height - labelHeight / 2 - 12, proposedCenterY));
      const boxX = labelCenterX - labelWidth / 2;
      const boxY = labelCenterY - labelHeight / 2;
      const labelAboveTarget = labelCenterY < targetY;
      const startY = labelAboveTarget ? boxY + labelHeight : boxY;
      const elbowY = startY + ((targetY - startY) * 0.48);
      const arrowDirection = targetY >= elbowY ? -1 : 1;
      const arrowBaseY = targetY + (arrowDirection * 7);

      callout.box.setAttribute('x', boxX.toFixed(2));
      callout.box.setAttribute('y', boxY.toFixed(2));
      callout.box.setAttribute('width', labelWidth.toFixed(2));
      callout.box.setAttribute('height', labelHeight.toFixed(2));
      callout.box.setAttribute('rx', mobile ? '7' : '6');
      callout.term.setAttribute('x', labelCenterX.toFixed(2));
      callout.term.setAttribute('y', labelCenterY.toFixed(2));
      callout.leader.setAttribute('points', `${labelCenterX.toFixed(2)},${startY.toFixed(2)} ${targetX.toFixed(2)},${elbowY.toFixed(2)} ${targetX.toFixed(2)},${targetY.toFixed(2)}`);
      callout.arrow.setAttribute('points', `${(targetX - 5).toFixed(2)},${arrowBaseY.toFixed(2)} ${targetX.toFixed(2)},${targetY.toFixed(2)} ${(targetX + 5).toFixed(2)},${arrowBaseY.toFixed(2)}`);
      callout.anchor.setAttribute('cx', targetX.toFixed(2));
      callout.anchor.setAttribute('cy', targetY.toFixed(2));
      callout.anchor.setAttribute('r', mobile ? '4.5' : '4');
      callout.svg.setAttribute('data-callout-part', button.dataset.partId || '');
      renderedCalloutButton = button;
      return true;
    };

    const resetLockedZoom = () => {
      root.classList.remove('has-locked-zoom');
      root.style.removeProperty('--nf-kp-focus-x');
      root.style.removeProperty('--nf-kp-focus-y');
    };

    const syncLockedZoom = (button) => {
      const hasVisibleHotspot = Boolean(
        button
        && lockedPart
        && button.dataset.hotspot === 'true'
        && button.dataset.partState === root.dataset.view,
      );
      if (!hasVisibleHotspot) {
        resetLockedZoom();
        return false;
      }
      const { x, y } = getHotspotCoordinates(button);
      root.style.setProperty('--nf-kp-focus-x', `${x}%`);
      root.style.setProperty('--nf-kp-focus-y', `${y}%`);
      root.classList.add('has-locked-zoom');
      return true;
    };

    const cancelMobileReturn = (force = false) => {
      if (!force && mobileReturnPhase !== 'scroll') return;
      mobileReturnToken += 1;
      if (mobileReturnFrame) window.cancelAnimationFrame(mobileReturnFrame);
      mobileReturnFrame = 0;
      mobileReturnPhase = 'idle';
    };

    const returnVisualAfterMobilePointerActivation = (button, shouldReturn) => {
      const compactPointerViewport = window.matchMedia('(max-width: 600px), (max-width: 700px) and (max-height: 500px)').matches;
      if (!shouldReturn || !visual || !compactPointerViewport) {
        syncLockedZoom(button);
        return;
      }
      cancelMobileReturn(true);
      const token = mobileReturnToken;
      resetLockedZoom();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const rect = visual.getBoundingClientRect();
      const usefulTop = 64;
      const usefulBottom = window.innerHeight - (window.innerHeight <= 500 ? 96 : 64);
      const visibleHeight = Math.max(0, Math.min(rect.bottom, usefulBottom) - Math.max(rect.top, usefulTop));
      const minimumUsefulHeight = Math.min(180, rect.height);
      const desiredTop = usefulTop + Math.max(0, (usefulBottom - usefulTop - rect.height) / 2);
      const maximumScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const targetScrollY = Math.max(0, Math.min(maximumScrollY, window.scrollY + rect.top - desiredTop));

      const applyZoom = () => {
        if (token !== mobileReturnToken) return;
        mobileReturnFrame = 0;
        mobileReturnPhase = 'idle';
        if (!reducedMotion) {
          visual.getBoundingClientRect();
          void visual.offsetHeight;
        }
        setCallout(button);
        syncLockedZoom(button);
      };

      if (visibleHeight >= minimumUsefulHeight) {
        mobileReturnPhase = 'zoom-pending';
        applyZoom();
        return;
      }
      if (reducedMotion) {
        window.scrollTo(0, targetScrollY);
        applyZoom();
        return;
      }

      const startScrollY = window.scrollY;
      const distance = targetScrollY - startScrollY;
      let startedAt = null;
      mobileReturnPhase = 'scroll';
      const step = (timestamp) => {
        if (token !== mobileReturnToken) return;
        if (startedAt === null) startedAt = timestamp;
        const progress = Math.min(1, (timestamp - startedAt) / 240);
        const eased = 1 - ((1 - progress) ** 3);
        window.scrollTo(0, startScrollY + distance * eased);
        if (progress < 1) {
          mobileReturnFrame = window.requestAnimationFrame(step);
          return;
        }
        mobileReturnFrame = 0;
        mobileReturnPhase = 'zoom-pending';
        applyZoom();
      };
      mobileReturnFrame = window.requestAnimationFrame(step);
    };

    const updateExplodeControl = () => {
      const isDrawn = root.dataset.view === 'drawn';
      const isExploded = root.dataset.exploded === 'true' && isDrawn;
      if (!explodeButton) return;
      explodeButton.hidden = !isDrawn;
      explodeButton.disabled = !isDrawn;
      explodeButton.setAttribute('aria-pressed', String(isExploded));
      if (explodeLabel) explodeLabel.textContent = isExploded ? root.dataset.explodeHide : root.dataset.explodeShow;
      if (explodedLabel) explodedLabel.hidden = !isExploded;
    };

    const setExploded = async (next, track = false) => {
      const shouldExplode = Boolean(next && root.dataset.view === 'drawn');
      if (shouldExplode && !(await ensureExplodedAssets())) return;
      root.dataset.exploded = String(shouldExplode);
      updateExplodeControl();
      const button = activeButton();
      if (button) {
        setCallout(button);
        syncLockedZoom(button);
      }
      if (track) {
        start();
        pushEvent('katana_parts_explorer_view_change', { view: shouldExplode ? 'drawn_exploded' : 'drawn_assembled' });
      }
    };

    const setView = (view) => {
      root.dataset.view = view;
      if (view !== 'drawn') root.dataset.exploded = 'false';
      canvases.forEach((canvas) => { canvas.hidden = canvas.dataset.viewCanvas !== view; });
      viewButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.viewControl === view)));
      if (viewLabel) viewLabel.textContent = view === 'drawn' ? root.dataset.sourceDrawn : root.dataset.sourceSheathed;
      updateExplodeControl();
      ensureViewAssets(view);
    };

    const resetDisplay = () => {
      definitions.forEach((card) => { card.hidden = true; });
      if (emptyPanel) emptyPanel.hidden = false;
      if (clearButton) clearButton.hidden = true;
      root.classList.remove('has-selection', 'has-hotspot');
      root.removeAttribute('data-active-part');
      resetLockedZoom();
      hideCallout();
      if (activeLabel) activeLabel.textContent = root.dataset.choosePart || '';
    };

    const renderPart = (button, { lock = false, method = 'preview', track = true, returnVisual = false } = {}) => {
      const partId = button.dataset.partId;
      const partName = button.dataset.partName;
      const partGroup = button.dataset.partGroup;
      const partState = button.dataset.partState;
      const completeGroup = button.dataset.completeGroup;
      if (lock) {
        lockedPart = partId;
        lastTrigger = button;
      }

      partButtons.forEach((candidate) => candidate.setAttribute('aria-pressed', String(candidate.dataset.partId === lockedPart && Boolean(lockedPart))));
      definitions.forEach((card) => { card.hidden = card.dataset.partId !== partId; });
      if (emptyPanel) emptyPanel.hidden = true;
      if (clearButton) clearButton.hidden = !lockedPart;
      root.dataset.activePart = partId;
      setCallout(button);
      if (activeLabel) activeLabel.textContent = `${partName}: ${button.dataset.partPlain}`;
      root.classList.add('has-selection');
      root.classList.toggle('has-hotspot', button.dataset.hotspot === 'true' && partState === root.dataset.view);

      if (lock) {
        returnVisualAfterMobilePointerActivation(button, returnVisual);
      }

      if (lock && track) {
        start();
        if (completeGroup) selectedGroups.add(completeGroup);
        pushEvent('katana_part_select', { part_name: partId, part_group: partGroup, interaction_method: method });
        if (!completed && ['blade', 'handle', 'saya'].every((group) => selectedGroups.has(group))) {
          completed = true;
          pushEvent('katana_parts_explorer_complete');
        }
      }
      if (lock && status) status.textContent = (root.dataset.statusSelected || '__PART__').replace('__PART__', partName);
    };

    const restoreLockedOrPrompt = () => {
      const button = activeButton();
      if (button && !button.hidden) renderPart(button, { lock: false, track: false });
      else resetDisplay();
    };

    const clearSelection = ({ focus = false, announce = true } = {}) => {
      const focusTarget = lastTrigger;
      lockedPart = '';
      partButtons.forEach((button) => button.setAttribute('aria-pressed', 'false'));
      resetDisplay();
      if (announce && status) status.textContent = root.dataset.statusCleared || '';
      if (focus && focusTarget) focusTarget.focus({ preventScroll: true });
    };

    const setGroup = (group) => {
      activeGroup = group;
      setView(group === 'saya' ? 'sheathed' : 'drawn');
      groupButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.groupFilter === group)));
      partButtons.forEach((button) => { button.hidden = button.dataset.filterGroup !== group; });
      const button = activeButton();
      if (!button || button.hidden) clearSelection({ announce: false });
      else renderPart(button, { lock: false, track: false });
    };

    groupButtons.forEach((button) => {
      button.addEventListener('click', () => {
        start();
        setGroup(button.dataset.groupFilter);
      });
    });

    viewButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const view = button.dataset.viewControl;
        start();
        setGroup(view === 'sheathed' ? 'saya' : 'blade');
        pushEvent('katana_parts_explorer_view_change', { view });
      });
    });

    if (explodeButton) explodeButton.addEventListener('click', () => setExploded(root.dataset.exploded !== 'true', true));
    if (clearButton) clearButton.addEventListener('click', () => clearSelection({ focus: true }));

    partButtons.forEach((button) => {
      button.addEventListener('pointerdown', (event) => {
        cancelMobileReturn(true);
        pointerActivation = {
          button,
          pointerId: event.pointerId,
          completed: false,
          startedAt: window.performance.now(),
        };
      });
      button.addEventListener('pointerup', (event) => {
        if (!pointerActivation || pointerActivation.button !== button || pointerActivation.pointerId !== event.pointerId) return;
        pointerActivation.completed = true;
      });
      button.addEventListener('pointercancel', (event) => {
        if (pointerActivation && pointerActivation.pointerId === event.pointerId) pointerActivation = null;
      });
      button.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') renderPart(button, { lock: false, method: 'hover', track: false });
      });
      button.addEventListener('pointerleave', restoreLockedOrPrompt);
      button.addEventListener('focus', () => renderPart(button, { lock: false, method: 'keyboard_focus', track: false }));
      button.addEventListener('blur', restoreLockedOrPrompt);
      button.addEventListener('click', (event) => {
        const pointerCompleted = Boolean(
          pointerActivation
          && pointerActivation.button === button
          && pointerActivation.completed
          && window.performance.now() - pointerActivation.startedAt < 1500,
        );
        const pointerLikeClick = pointerCompleted || event.detail > 0;
        pointerActivation = null;
        renderPart(button, {
          lock: true,
          method: pointerLikeClick ? 'click' : 'keyboard',
          returnVisual: pointerLikeClick,
        });
      });
    });

    window.addEventListener('wheel', cancelMobileReturn, { passive: true });
    window.addEventListener('pointerdown', cancelMobileReturn, { passive: true });
    window.addEventListener('keydown', cancelMobileReturn);

    if (visual && 'ResizeObserver' in window) {
      const calloutObserver = new ResizeObserver(() => {
        if (renderedCalloutButton) setCallout(renderedCalloutButton);
      });
      calloutObserver.observe(visual);
    }

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      clearSelection({ focus: true });
    });

    const hashPartId = window.location.hash.slice(1);
    const hashButton = partButtons.find((button) => button.dataset.partId === hashPartId);
    if (hashButton) {
      setGroup(hashButton.dataset.filterGroup);
      renderPart(hashButton, { lock: true, method: 'anchor', track: false });
    } else {
      setGroup(activeGroup);
    }
  };

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        initialize(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '240px 0px' });
    roots.forEach((root) => observer.observe(root));
  } else {
    roots.forEach(initialize);
  }
})();
